<template>
  <div class="grid-shipments">
    <h3 class="page-title grid-title">{{ title }}</h3>
    <ag-grid-vue
      class="ag-theme-balham"
      data-cy="order-shipments-grid"
      :grid-options="gridOptions"
      :column-defs="columnDefinitions"
      :default-col-def="defaultColDef"
      :row-data="rowData"
      :pagination="true"
      :pagination-page-size="pageSize"
      :suppress-menu-hide="true"
      :modules="modules"
      :overlay-no-rows-template="`<span>${$t('viewOrder.shipments.no_shipments_yet')}</span>`"
      v-on:first-data-rendered="onFirstDataRendered"
      v-on:grid-ready="onGridReady">
    </ag-grid-vue>
  </div>
</template>

<script lang="ts">
import { useDocumentHelpers } from '@/composables/document-helpers';
import { handleTrackingLinkClick, renderTrackingButton } from '@/composables/utils';
import { ordersMixins } from '@/mixins/orders.mixins';
import { utilsMixins } from '@/mixins/utils.mixins';
import { OrderDocumentType } from '@/models/order-document-type.enum';
import { OrderDocument } from '@/models/order-document.model';
import { OrderDocumentService } from '@/services/order-document-service';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { GridApi, type CellClickedEvent, type ColDef } from '@ag-grid-community/core';
import { AgGridVue } from '@ag-grid-community/vue3';
import type { OrderDocumentModel, PackingSlipTrackingInformationModel } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { useGrid } from '../../composables/grid';
import { useLocale } from '../../composables/locale';
import { Order } from '../../models/order.model';
import { useUserStore } from '../../stores/user';

export default defineComponent({
  name: 'grid-order-shipments',
  components: {
    AgGridVue,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { selectedAccount } = storeToRefs(useUserStore());
    const { openPDF } = useDocumentHelpers();
    const { userLocale } = useLocale();
    const { defaultGridOptions, dateColumnDefinition, applyGridConfig } = useGrid();

    return {
      account: selectedAccount,
      userLocale,
      openPDF,
      defaultGridOptions,
      dateColumnDefinition,
      applyGridConfig,
    };
  },
  data() {
    const gridOptions = this.defaultGridOptions();
    gridOptions.onPaginationChanged = this.onPaginationChanged;

    return {
      gridOptions,
      orderDocumentService: new OrderDocumentService(),
      documents: new Array<OrderDocument>(),
      columnDefinitions: new Array<any>(),
      rowData: new Array<any>(),
      gridApi: undefined as GridApi | undefined,
      modules: [ClientSideRowModelModule],
      defaultColDef: {
        resizable: true,
        filter: true,
        sortable: true,
      },
      pageSize: 5,
      rowPixelsOffset: 1,
      totalRows: undefined as undefined | number,
      noRowsTemplate: `<span>${this.$t('viewOrder.shipments.no_shipments_yet')}</span>`,
    };
  },
  computed: {
    title(): string {
      return `${this.$t('viewOrder.shipments.title')} ( ${this.totalRows ?? ''} )`;
    },
  },
  watch: {
    userLocale(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.gridApi?.redrawRows();
          this.gridApi?.refreshHeader();
        });
      }
    },
  },
  mounted() {
    this.loadShipmentsGrid();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    this.gridApi = undefined;
  },
  methods: {
    ...ordersMixins,
    ...utilsMixins,
    loadShipmentsGrid() {
      const columnDefinitions: ColDef[] = [
        {
          headerValueGetter: () => this.$t('viewOrder.shipments.columns.shipment_number'),
          field: 'shipmentNumber',
          onCellClicked: (params: any) => {
            this.$emit('shipment-selected', params.value);
          },
          cellStyle: { textAlign: 'left' },
          flex: 1,
          cellClass: ['clickable-cell'],
        },
        {
          headerValueGetter: () => this.$t('viewOrder.shipments.columns.quantity_shipped'),
          field: 'quantity',
          cellStyle: { textAlign: 'left' },
        },
        {
          headerValueGetter: () => this.$t('viewOrder.shipments.columns.carrier_service'),
          field: 'carrier',
          cellStyle: { textAlign: 'left' },
        },
        {
          headerValueGetter: () => this.$t('viewOrder.tracking'),
          field: 'trackingLink',
          cellRenderer: renderTrackingButton,
          onCellClicked: handleTrackingLinkClick,
          filter: false,
          cellStyle: { textAlign: 'center' },
        },
        {
          headerValueGetter: () => this.$t('viewOrder.shipments.columns.packing_slip'),
          field: 'shipmentDownload',
          cellRenderer: this.shipmentRenderer,
          cellStyle: { textAlign: 'left' },
          filter: false,
          flex: 1, // Fill rest of the grid when rows are empty and autosize fails
        },
      ];

      this.columnDefinitions = columnDefinitions;
      this.rowData = [];

      (this.order as Order).packingSlips?.map((packingSlip) => {
        this.rowData.push({
          shipmentNumber: packingSlip.deliveryNumber || '',
          quantity: packingSlip.documentLines?.reduce(
            (accumulator, currentValue) => accumulator + currentValue.quantityDelivered,
            0,
          ),
          carrier: packingSlip.shipCarrierId,
          trackingLink: this.getTrackingCellRenderData(packingSlip),
          shipmentDownload: packingSlip,
        });
      });
    },
    getTrackingCellRenderData(packingSlip: OrderDocumentModel) {
      const packingSlipTrackingInfo: PackingSlipTrackingInformationModel | undefined = (
        this.order as Order
      ).packingSlipTrackingInfo?.find(
        (slipTracker: PackingSlipTrackingInformationModel) =>
          slipTracker.packingSlipNumber === packingSlip.deliveryNumber,
      );

      return packingSlipTrackingInfo?.trackingURL || packingSlipTrackingInfo?.trackingNumber;
    },
    onFirstDataRendered(params: any) {
      this.gridApi = params.api;
      params.api.sizeColumnsToFit();
    },
    handleResize() {
      this.gridApi?.sizeColumnsToFit();
    },
    onGridReady(grid: any) {
      this.gridApi = grid.api;

      grid.api.addEventListener('cellClicked', this.onCellClick);
    },
    async onCellClick(params: CellClickedEvent) {
      if (params.colDef.field === 'shipmentDownload') {
        params.data.loading = true;
        params.node.setData(params.data);
        this.$nextTick(() => this.gridApi?.redrawRows());

        const validation = await this.orderDocumentService.get(
          params.value.orderNumber,
          params.value.id,
          this.account.id,
          this.getDocumentType(OrderDocumentType.PackingSlip),
        );

        if (validation.isValid() && validation.model?.base64Body) {
          this.openPDF(validation.model.base64Body, params.data.shipmentNumber, params.value.orderNumber);
        } else {
          this.$snotify.warning(this.$t('documentsCheckout.document_is_not_available'));
        }

        params.data.loading = false;
        params.node.setData(params.data);
        this.$nextTick(() => this.gridApi?.redrawRows());
      }
    },

    shipmentRenderer(params: any) {
      const getDownloadButton = (isReadyForDownload: boolean, isLoading: boolean) => {
        return isReadyForDownload
          ? `<button class="${isLoading ? 'button download-button submitting' : 'button download-button'}"></button>`
          : '';
      };
      const downloadButton = `<span class=shipment-renderer--action>${getDownloadButton(params.value.isReadyForDownload, params.data.loading)}</span>`;
      const buttonText = `<span class=shipment-renderer--text>${this.getOrderDocumentDownloadButtonText(params.value, this.$t, this.userLocale)}</span>`;

      return `<div class='shipment-renderer'>${downloadButton}${buttonText}</div>`;
    },

    onPaginationChanged(gridOptions: any) {
      this.totalRows = gridOptions.api?.paginationGetRowCount();
    },
  },
});
</script>

<style scoped lang="scss">
.grid-shipments:deep() {
  .button.download-button {
    display: inline;
  }

  .button.download-button.submitting {
    padding: 15px;
    background-position: 30% center;
  }

  .tracking-button {
    display: inline;
  }

  .shipment-renderer {
    display: flex;
    justify-content: center;
    align-items: center;
    &--text {
      padding-bottom: 6px;
    }
  }
}
</style>
